import React, { useContext } from 'react'
import { getBadgeTheme, getProductTypeTags } from '@helpers/products'
import StarRatings from '@components/ui/StarRatings'
import PriceComparison from '@components/ui/PriceComparison'
import Badge, { BadgeType } from '@components/ui/Badge'
import ProductTitle from '@components/ui/ProducTitle'
import { BREAKPOINT_SIZES } from '@consts/index'
import classNames from 'classnames'
import { ProductWithVariants } from 'types/product'
import ProductContext from '../context'
import productHeader from './productHeader.module.scss'
import { useMediaQuery } from '@helpers/hooks'
import useBrowsingInNativeCurrency from '@helpers/hooks/useBrowsingInNativeCurrency'
import useLocalisedPrice from '@helpers/hooks/useLocalisedPrice'

const BADGE_TYPE_RULES: {
  exact: Record<string, 'outline'>
  patterns: (string | RegExp)[]
} = {
  exact: {
    organic: 'outline'
  },
  patterns: ['priority', /beta/i, /pro/i]
}

const resolveBadgeType = (raw: string, formatted: string): BadgeType => {
  const rawLower = raw.toLowerCase()

  const exactType =
    BADGE_TYPE_RULES.exact[rawLower as keyof typeof BADGE_TYPE_RULES.exact]

  if (exactType) return exactType

  const patternMatch = BADGE_TYPE_RULES.patterns.some(pattern =>
    typeof pattern === 'string'
      ? rawLower.includes(pattern)
      : pattern.test(formatted)
  )

  if (patternMatch) return 'outline'

  return 'filled'
}

type ProductHeaderProps = {
  product: ProductWithVariants
  byline: string
  classes?: string
  starsAverage?: number
  totalReviews?: number
  onStarsClick?: () => void
}

const ProductHeader = ({
  product,
  byline = '',
  classes = undefined,
  starsAverage,
  totalReviews,
  onStarsClick
}: ProductHeaderProps) => {
  const { discount } = useContext(ProductContext)
  const browsingInNativeCurrency = useBrowsingInNativeCurrency()
  const { price, fullPrice, currency, isLoading } = useLocalisedPrice(product.id, product.price, product.compare_at_price)
  const isMobileDown = useMediaQuery({ maxWidth: BREAKPOINT_SIZES.TABLET - 1 })

  const productTags = product?.tags?.split(',') ?? []
  const productTypeTags = getProductTypeTags(productTags)

  const productHeaderContainer = classNames(
    productHeader.productHeader__container,
    classes
  )

  const badgeProps = ({
    raw,
    formatted
  }: {
    raw: string
    formatted: string
  }) => {
    const badgeType = resolveBadgeType(raw, formatted)

    return {
      title: formatted,
      themeColour: getBadgeTheme({ raw, formatted }),
      badgeType: badgeType,
      ...(isMobileDown ? { smallX2: true } : { medium: true })
    }
  }

  return (
    <div className={productHeaderContainer}>
      <div className={productHeader.productHeader__tagTop}>
        {productTypeTags.map(tag => (
          <Badge key={tag.raw} {...badgeProps(tag)} small />
        ))}
        {browsingInNativeCurrency && discount.title && (
          <Badge
            badgeType="filled"
            themeColour={getBadgeTheme({
              raw: 'discount',
              formatted: 'discount'
            })}
            title={discount.title}
            small
          />
        )}
      </div>
      <header className={productHeader.productHeader__headlineContainer}>
        <h1 className={productHeader.productHeader__title}>
          <ProductTitle text={product.display_name} />
        </h1>{' '}
      </header>
      <h3 className={productHeader.productHeader__byline}>{byline}</h3>
      <div className={productHeader.productHeader__row}>
        {starsAverage !== undefined &&
          (onStarsClick ? (
            <button
              type="button"
              onClick={onStarsClick}
              className={productHeader.productHeader__starsButton}
            >
              <StarRatings
                averageScore={starsAverage}
                totalReviews={totalReviews}
              />
            </button>
          ) : (
            <a href="#trustpilot-reviews">
              <StarRatings
                averageScore={starsAverage}
                totalReviews={totalReviews}
              />
            </a>
          ))}
        {!!product.price && (
          <div className={productHeader.ProductHeader__prices}>
            <PriceComparison
              price={price}
              fullPrice={fullPrice}
              currency={currency}
              isLoading={isLoading}
              size="medium-bold"
            />
          </div>
        )}
      </div>
    </div>
  )
}

export default ProductHeader
